/*
 * Account API Routes
 * 
 * Features:
 * - Quick search accounts (search endpoint with caching) - UNIVERSAL ACCESS
 * - Get account counts grouped by team (count endpoint for efficient statistics) - UNIVERSAL ACCESS
 * - CRUD operations for accounts (get, create, update, delete) - UNIVERSAL VIEW, TEAM EDIT
 * - Get accounts by user or team - UNIVERSAL ACCESS
 * - Account-specific data retrieval (contacts, related summaries) - UNIVERSAL ACCESS
 * 
 * Implementation:
 * - Read operations now allow universal access to all accounts for viewing
 * - Write operations (create, update, delete) still respect user team permissions
 * - RLP accounts (rlpCompanyId) and team accounts are merged/queried appropriately
 * - Count endpoint avoids fetching full account data for dashboard statistics
 * - Search endpoint has server and client caching for performance
 * 
 * Changes:
 * - 2026-09-09: Counts only readable notes; fresh reads honor current user access levels.
 * - 2025-10-31: Added GET /api/data/accounts/count endpoint to return counts by teamId
 * - 2026-01-26: Updated all read endpoints to use universal access (removed CurrentUserAndTeams filtering)
 */
import { z } from "zod";
import { DataRoutes } from "./types";

const accountRoutes: DataRoutes = {

  // Quick search accounts
  'get /api/data/accounts/search': {
    schema: z.object({
      q: z.string()
    }),
    query: `EXEC [dbo].[SearchAllUniversal] @searchString = @q, @resultType = 'BOTH', @matchMode='ALL'`,
    serverCache: 10, // 10 seconds
    clientCache: 10, // 10 seconds
  },

  // Get total count of accounts in system
  'get /api/data/accounts/count': {
    query: `SELECT
      ISNULL(A.teamId, 0) AS teamId
      ,COUNT(*) AS count
      FROM dbo.Accounts A
      GROUP BY A.teamId
    `
  },

  // Get list of all accounts
  'get /api/data/accounts': {
    cache: false,
    query: `SELECT
      a.id,
      a.name,
      dbo.MergeJsonObjects(a.data, (SELECT *
        FROM RLPCompanies
        WHERE RLPCompanies.CompanyId = a.rlpCompanyId
        FOR JSON PATH, WITHOUT_ARRAY_WRAPPER)) as data,
      a.rlpCompanyId,
      a.teamId,
      count(b.noteId) as noteCount
    FROM [dbo].accounts a
    LEFT JOIN NoteAccounts b ON a.id = b.accountId
      AND EXISTS (SELECT 1 FROM dbo.fnVisibleNoteIds(dbo.fnGetContextUser()) V WHERE V.Id = b.NoteId)
    GROUP BY a.id, a.name, a.data, a.rlpCompanyId, a.teamId`,
  },

  // Get details of a specific account
  'get /api/data/accounts/:id': {
    schema: z.object({
      id: z.number().int(),
    }),
    queryOne: `SELECT
      a.id,
      a.name,
      a.rlpCompanyId,
      a.teamId,
      a.createdBy,
      dbo.MergeJsonObjects(a.data, (SELECT *
        FROM RLPCompanies
        WHERE RLPCompanies.CompanyId = a.rlpCompanyId
        FOR JSON PATH, WITHOUT_ARRAY_WRAPPER)) as data
    FROM [dbo].accounts a
    WHERE id = @id`,
  },

  // Create a new account with provided details
  'post /api/data/accounts': {
    schema: z.object({
      name: z.string(),
      data: z.string(),
      teamId: z.number()
    }),
    query: `INSERT INTO [dbo].[Accounts] (name, data, teamId, createdBy)
    VALUES (@name, @data, @teamId, dbo.fnGetContextUser())`
  },
  // Update specified fields of an existing account
  'patch /api/data/accounts/:id': {
    schema: z.object({
      id: z.number().int(),
      name: z.string(),
      data: z.string().optional()
    }),
    query: `UPDATE [dbo].[Accounts]
            SET 
                name = @name,
                data = dbo.MergeJsonObjects(data, @data),
                updatedBy = dbo.fnGetContextUser(),
                updatedAt = GETDATE()
            WHERE id = @id`
  },

  // Get user accounts
  'get /api/data/users/:userId/accounts': {
    cache: false,
    schema: z.object({
      userId: z.number().int(),
    }),
    query: `SELECT
      a.id,
      a.name,
      dbo.MergeJsonObjects(a.data, (SELECT *
        FROM RLPCompanies
        WHERE RLPCompanies.CompanyId = a.rlpCompanyId
        FOR JSON PATH, WITHOUT_ARRAY_WRAPPER)) as data,
      a.rlpCompanyId,
      a.teamId,
      count(b.noteId) as noteCount
    FROM [dbo].accounts a
    LEFT JOIN NoteAccounts b ON a.id = b.accountId
      AND EXISTS (SELECT 1 FROM dbo.fnVisibleNoteIds(dbo.fnGetContextUser()) V WHERE V.Id = b.NoteId)
    JOIN [UserAccounts] ua ON a.id = ua.accountId
    WHERE userId = @userId
    GROUP BY a.id, a.name, a.data, a.rlpCompanyId, a.teamId
    `
  },

  // Get accounts filtered by team or RLP (used by All Accounts view with schema selector)
  // teamId = -1: Returns legacy RLP accounts (rlpCompanyId IS NOT NULL)
  // Get accounts for a specific team by teamId (for team-specific or RLP views)
  // teamId > 0: Returns team-specific accounts (teamId = @teamId)
  'get /api/data/team/:teamId/accounts': {
    cache: false,
    schema: z.object({
      teamId: z.number().int(),
    }),
    query: `SELECT
      a.id,
      a.name,
      dbo.MergeJsonObjects(a.data, (SELECT *
        FROM RLPCompanies
        WHERE RLPCompanies.CompanyId = a.rlpCompanyId
        FOR JSON PATH, WITHOUT_ARRAY_WRAPPER)) as data,
      a.rlpCompanyId,
      a.teamId,
      count(b.noteId) as noteCount
    FROM [dbo].accounts a
    LEFT JOIN NoteAccounts b ON a.id = b.accountId
      AND EXISTS (SELECT 1 FROM dbo.fnVisibleNoteIds(dbo.fnGetContextUser()) V WHERE V.Id = b.NoteId)
    WHERE (@teamId = -1 AND a.rlpCompanyId IS NOT NULL)
       OR (@teamId > 0 AND a.teamId = @teamId)
    GROUP BY a.id, a.name, a.data, a.rlpCompanyId, a.teamId
    ORDER BY a.name
    `
  },


  // Delete an account by its ID
  'delete /api/data/accounts/:id': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `DELETE FROM [dbo].[Accounts] WHERE id = @id
    AND createdBy = dbo.fnGetContextUser()
    `
  },
};

export default accountRoutes;
